import React from 'react';
import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import {PageHeader} from '../PageHeader';
import {useGetTagsWithDetails} from '../../hooks/api/tags';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {TagDetailsResponse} from '../../shared/tags';
import {pluralize} from '../../lib/helpers';
import {TagEditorModal} from './TagEditorModal';
import {CaretDown, CaretUp, OverflowMenuVertical} from '@carbon/icons-react';
import {useNavigate} from 'react-router-dom';
import {EnrichAccountsModal} from '../AccountGrid/header/EnrichButton';
import {useIsKeyplayAdmin} from '../../hooks/api/user';
import {useScoringInfo} from '../../hooks/api/scoringInfo';
import {DeleteTagModal} from './DeleteTagModal';
import {WebResearcherIcon} from '../Icons/WebResearcher';
import {CustomerAccountsImportModal} from '../Accounts/CustomerAccountsImport';
import {useIsFeatureEnabled} from '../../hooks/api/metadata';
import {useAccountLimitsForImport} from '../../hooks/useAccountLimitsForImport';

const EmptyAccountLists = () => {
  return (
    <Flex
      direction="column"
      align="center"
      p="16"
      gap="4"
      maxW="600px"
      alignSelf="center"
    >
      <WebResearcherIcon h="160px" w="160px" />
      <Text>No tags created yet</Text>
      <Text textColor="kgray.300" textAlign="center">
        {/* TODO: Update this message once list importing is implemented. Check: https://www.figma.com/design/YVh0Moky90ZA152KecmOo1/Keyplay-v1.5--2024-?node-id=3872-49056&t=lMhzBORYxHazWS3V-0 */}
        Create a new tag to manually add to your existing accounts. You can then
        filter and organize accounts by these tags.
      </Text>
    </Flex>
  );
};

export const AccountLists = () => {
  const tagsDetails = useGetTagsWithDetails({});
  const createTagModal = useDisclosure();
  const importModal = useDisclosure();
  const showImportModal = useIsFeatureEnabled('importsTab');
  const accountLimits = useAccountLimitsForImport();

  if (tagsDetails.isLoading) {
    return <InitialLoading />;
  }

  return (
    <Flex direction="column">
      <PageHeader title="Account Lists">
        <Flex gap="2">
          <Button
            colorScheme="kbuttonblue"
            fontWeight="normal"
            variant="outline"
            onClick={createTagModal.onOpen}
          >
            Create New Tag
          </Button>
          <TagEditorModal
            isOpen={createTagModal.isOpen}
            onClose={createTagModal.onClose}
          />

          {showImportModal && (
            <>
              <Tooltip
                label={
                  accountLimits.isAtLimit
                    ? accountLimits.limitMessage
                    : undefined
                }
                isDisabled={!accountLimits.isAtLimit}
              >
                <Button
                  colorScheme="kbuttonblue"
                  fontWeight="normal"
                  onClick={importModal.onOpen}
                  isDisabled={accountLimits.isAtLimit}
                >
                  Import List
                </Button>
              </Tooltip>
              <CustomerAccountsImportModal
                isOpen={importModal.isOpen}
                onClose={importModal.onClose}
              />
            </>
          )}
        </Flex>
      </PageHeader>

      <Flex
        direction="column"
        gap="4"
        p="4"
        w="100%"
        h="100%"
        maxW="1000px"
        alignSelf="center"
      >
        <Box textColor="kgray.300" fontSize="sm">
          {pluralize(tagsDetails.data?.length ?? 0, 'list', 'lists')}
        </Box>

        <AccountListsTable tags={tagsDetails.data ?? []} />
        {!tagsDetails.data?.length && <EmptyAccountLists />}
      </Flex>
    </Flex>
  );
};

const columnHelper = createColumnHelper<TagDetailsResponse>();
const columns = [
  columnHelper.accessor('name', {
    header: 'Tag Name',
    size: 500,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('numAccounts', {
    id: 'accounts',
    header: 'Accounts',
    size: 30,
    cell: (info) => (
      <Text color="kgray.300">{(info.getValue() ?? 0).toLocaleString()}</Text>
    ),
  }),
  columnHelper.accessor('previewAccounts', {
    id: 'preview',
    header: 'Preview',
    size: 140,
    cell: (info) => <TagAccountsPreview previewAccounts={info.getValue()} />,
    enableSorting: false,
  }),
  columnHelper.accessor((tag) => tag, {
    id: 'actions',
    header: '',
    size: 30,
    cell: (info) => <TagMenu tag={info.getValue()} />,
    enableSorting: false,
  }),
];

const TagMenu = ({tag}: {tag: TagDetailsResponse}) => {
  const tagEditorModal = useDisclosure();
  const deleteTagModal = useDisclosure();
  const enrichAccountsModal = useDisclosure();
  const navigate = useNavigate();
  const isAdmin = useIsKeyplayAdmin();

  const handleViewAccounts = () => {
    const queryParams = new URLSearchParams();
    queryParams.set('tag', tag._id.toString());
    navigate({pathname: '/accounts/all', search: queryParams.toString()});
  };
  const {data: scoringInfo} = useScoringInfo();
  const scoringRunId = scoringInfo?.latestScoringRunId;

  return (
    <Box textAlign="end">
      <Menu>
        <MenuButton as={Button} variant="ghost" size="sm" p="2">
          <OverflowMenuVertical />
        </MenuButton>
        <MenuList>
          <MenuItem onClick={tagEditorModal.onOpen}>
            Edit Tag Name
            <TagEditorModal
              tag={tag}
              isOpen={tagEditorModal.isOpen}
              onClose={tagEditorModal.onClose}
            />
          </MenuItem>
          <MenuItem textColor="kred.500" onClick={deleteTagModal.onOpen}>
            Delete Tag
            <DeleteTagModal
              tag={tag}
              isOpen={deleteTagModal.isOpen}
              onClose={deleteTagModal.onClose}
            />
          </MenuItem>
          <MenuDivider mx="3" />
          <MenuItem onClick={handleViewAccounts}>View Accounts</MenuItem>
          {isAdmin && (
            <MenuItem
              onClick={enrichAccountsModal.onOpen}
              isDisabled={!scoringRunId}
            >
              Enrich Accounts
              {scoringRunId && (
                <EnrichAccountsModal
                  accountSelection={{
                    name: 'query',
                    query: {
                      filter: {
                        tags: [tag._id],
                      },
                      runId: scoringRunId,
                    },
                  }}
                  isOpen={enrichAccountsModal.isOpen}
                  onClose={enrichAccountsModal.onClose}
                />
              )}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

const TagAccountsPreview = ({
  previewAccounts,
}: {
  previewAccounts: TagDetailsResponse['previewAccounts'];
}) => {
  if (!previewAccounts?.length) {
    return null;
  }

  return (
    <Flex gap="1">
      {previewAccounts.map((account) => (
        <Tooltip
          key={account._id.toString()}
          label={account.name}
          placement="top"
        >
          <Box borderRadius="md" overflow="hidden" flexShrink="0">
            <Image
              src={account.logoUrl}
              alt={account.name}
              boxSize="24px"
              fallbackSrc="/accountFallback.png"
            />
          </Box>
        </Tooltip>
      ))}
    </Flex>
  );
};

const AccountListsTable = ({tags}: {tags: TagDetailsResponse[]}) => {
  const [sorting, setSorting] = React.useState([{id: 'accounts', desc: true}]);

  const table = useReactTable({
    columns,
    data: tags ?? [],
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row._id.toString(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: tags.length > 0,
    state: {sorting},
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    defaultColumn: {
      minSize: 0,
      size: 150,
    },
  });

  return (
    <TableContainer border="1px" borderColor="kgray.200" borderRadius="md">
      <Table variant="simple">
        <Thead bg="kgray.100">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  w={header.getSize() ? `${header.getSize()}px` : 'auto'}
                  cursor={header.column.getCanSort() ? 'pointer' : undefined}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <Flex alignItems="center" gap="1">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getCanSort() && (
                      <Box display="inline-block">
                        {{
                          asc: <CaretUp />,
                          desc: <CaretDown />,
                        }[header.column.getIsSorted().toString()] ?? ''}
                      </Box>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id} role="group">
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  _groupHover={{bg: 'kgray.100'}}
                  w={
                    cell.getContext().column.getSize()
                      ? `${cell.getContext().column.getSize()}px`
                      : 'auto'
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const InitialLoading = () => {
  return (
    <Flex flex="1">
      <AbsoluteCenter>
        <Spinner color="kred.300" size="lg" />
      </AbsoluteCenter>
    </Flex>
  );
};
